export function buildInvoiceHTML(invoice = {}, settings = {}, paymentUrl = null) {
  const { invoiceNumber = 'PREVIEW', clientName = '', clientEmail = '', clientCompany = '', items = [], subtotal = 0, discount = 0, tax = 0, total = 0, dueDate } = invoice;
  const companyName = settings?.companyName || 'PageHyve Studios';
  const companyEmail = settings?.billingEmail || settings?.email || 'billing@example.com';
  const companyPhone = settings?.billingPhone || settings?.phone || '+1 555-0100';
  const companyAddress = settings?.billingAddress || settings?.address || '';
  const contactLine = (() => {
    const secondary = [settings?.billingEmail || settings?.email || '', settings?.billingPhone || settings?.phone || ''].filter(Boolean).join(' • ');
    return secondary ? `${companyAddress ? companyAddress + '<br>' : ''}${secondary}` : (companyAddress || '');
  })();

  const formattedItems = items.map(it => `
    <tr>
      <td style="padding:8px;border:1px solid #ddd">${escapeHtml(it.name)}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:right">${it.quantity}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:right">$${Number(it.rate).toFixed(2)}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:right">$${(Number(it.rate) * Number(it.quantity)).toFixed(2)}</td>
    </tr>`).join('');

  const html = `<!doctype html>
  <html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <title>Invoice ${escapeHtml(invoiceNumber)}</title>
    <style>
      :root{--bg:#fff;--muted:#64748b;--accent:#34d399}
      body{font-family: -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;margin:0;padding:1rem;background:#f3f4f6}
      .sheet{max-width:800px;margin:0 auto;background:var(--bg);padding:1.5rem;border-radius:6px}
      header{display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem}
      h1{margin:0;font-size:1.25rem}
      table{width:100%;border-collapse:collapse;margin-top:1rem}
      tfoot td{font-weight:700}
      .meta{font-size:0.9rem;color:var(--muted)}
      .print-btn{display:inline-block;margin-top:1rem;padding:0.5rem 1rem;background:var(--accent);color:#020617;border-radius:6px;text-decoration:none}
      @media print{body{background:#fff} .print-btn{display:none}}
    </style>
  </head>
  <body>
    <div class="sheet">
      <header>
        <div style="display:flex;align-items:center;gap:0.75rem">
          <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256' role='img' aria-label='PageHyve icon'%3E%3Cpolygon fill='%234FAFB3' points='96.0,52.0 256.0,52.0 220.0,92.0 60.0,92.0' /%3E%3Cpolygon fill='%230F172A' points='84.0,106.0 244.0,106.0 208.0,146.0 48.0,146.0' /%3E%3Cpolygon fill='%230F172A' points='72.0,160.0 232.0,160.0 196.0,200.0 36.0,200.0' /%3E%3C/svg%3E" alt="PageHyve" style="width:60px;height:60px" />
          <div>
            <h1>${escapeHtml(companyName)}</h1>
            <div class="meta">${contactLine ? contactLine : ''}</div>
          </div>
        </div>
        <div style="text-align:right">
          <div style="font-size:0.9rem;color:var(--muted)">Invoice</div>
          <div style="font-weight:700;font-size:1.125rem">${escapeHtml(invoiceNumber)}</div>
          <div class="meta">Due: ${dueDate ? new Date(dueDate).toLocaleDateString() : ''}</div>
        </div>
      </header>

      <section style="display:flex;justify-content:space-between;gap:1rem">
        <div>
          <strong>Bill To</strong>
          <div style="margin-top:0.25rem">${escapeHtml(clientName)}</div>
          <div class="meta">${escapeHtml(clientCompany)}${clientEmail ? ' • ' + escapeHtml(clientEmail) : ''}</div>
        </div>
        <div style="text-align:right">
          <div class="meta">Issued: ${new Date().toLocaleDateString()}</div>
        </div>
      </section>

      <table>
        <thead>
          <tr>
            <th style="padding:8px;border:1px solid #ddd;text-align:left">Description</th>
            <th style="padding:8px;border:1px solid #ddd;text-align:right">Qty</th>
            <th style="padding:8px;border:1px solid #ddd;text-align:right">Rate</th>
            <th style="padding:8px;border:1px solid #ddd;text-align:right">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${formattedItems}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="3" style="padding:8px;border:1px solid #ddd;text-align:right">Subtotal</td>
            <td style="padding:8px;border:1px solid #ddd;text-align:right">$${Number(subtotal).toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="3" style="padding:8px;border:1px solid #ddd;text-align:right">Discount</td>
            <td style="padding:8px;border:1px solid #ddd;text-align:right">$${Number(discount).toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="3" style="padding:8px;border:1px solid #ddd;text-align:right">Tax</td>
            <td style="padding:8px;border:1px solid #ddd;text-align:right">$${Number(tax).toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="3" style="padding:8px;border:1px solid #ddd;text-align:right">Total</td>
            <td style="padding:8px;border:1px solid #ddd;text-align:right">$${Number(total).toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>

      <div style="margin-top:1rem;display:flex;justify-content:space-between;align-items:center">
        <div>
          <a class="print-btn" href="#" onclick="window.print();return false">Print Invoice</a>
          ${paymentUrl ? `<a class="print-btn" href="${paymentUrl}" style="margin-left:0.5rem;">Pay Now</a>` : ''}
        </div>
        <div class="meta">Thank you for your business.</div>
      </div>
    </div>
  </body>
  </html>`;

  return html;
}

function escapeHtml(str){
  if (str === undefined || str === null) return '';
  return String(str).replace(/[&<>"'`=\/]/g, s => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;','`':'&#96;','=':'&#61;','/':'&#47;'})[s]);
}
